import { useState } from 'react'

import { Box, Form, Text, Title } from '@factorialco/deprecated-design-system'
import {
  CategoryFragment,
  EditTrainingTrainingFragment,
  ProcessFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FundaeToggle } from 'modules/trainings/components/Revamp/FundaeToggle'
import { SubsidizeToggle } from 'modules/trainings/components/Revamp/SubsidizeToggle'
import UnlinkWorkflowsModal from 'modules/trainings/components/Revamp/TrainingEditMode/UnlinkWorkflowsModal'
import { InternalInfoFormValues } from 'modules/trainings/components/Revamp/TrainingEditMode/types'
import { WorkflowToggle } from 'modules/trainings/components/Revamp/WorkflowToggle'
import { useEditTrainingContext } from 'modules/trainings/hooks/EditTraining/useEditTrainingContext'
import { useUpdateTrainingInternalInfo } from 'modules/trainings/hooks/Trainings/useUpdateTrainingInternalInfo'
import { useUpsertTrainingSubsidy } from 'modules/trainings/hooks/Trainings/useUpsertTrainingSubsidy'
import {
  externalBoolToString,
  externalOptions,
  externalStringToBool,
} from 'modules/trainings/lib/trainingsForm'

export const InternalInfo = ({
  categories,
  processes,
  saved,
  training,
}: {
  categories: CategoryFragment[]
  processes: ProcessFragment[]
  saved: boolean
  training: EditTrainingTrainingFragment
}) => {
  const [openModal, setOpenModal] = useState(false)
  const { company: currentCompany } = useCurrentSession()
  const { setIsDirty, setIsDisabled, setSaved } = useEditTrainingContext()
  const { updateInternalInfoTraining, loading } = useUpdateTrainingInternalInfo({
    onCompleted(data) {
      if (data.trainingsMutations.updateInternalInfoTrainingsTraining?.errors.length) {
        return
      }
    },
  })
  const { upsertTrainingSubsidy } = useUpsertTrainingSubsidy()
  const fundaeProcess = processes
    ? processes.find((process) => process.template?.id === 'fundae')
    : undefined

  const readonly = !training._policies?.edit.can

  const { values, resetForm, isSubmitting, handleSubmit, dirty, errors, setValues, setErrors } =
    useForm<InternalInfoFormValues>({
      defaultValues: {
        code: String(training?.code || ''),
        external: Boolean(training?.external),
        externalProvider: String(training?.externalProvider),
        categoryIds: training.categories?.map((category) => category.id) || [],
        year: training.year || moment().year(),
        processId: training.processId || undefined,
        codigoCurso: training.codigoCurso || undefined,
        perfil: training.perfil || undefined,
        expediente: training.expediente || undefined,
        fundaeSubsidized: training.fundaeSubsidized,
        subsidized: training.subsidized,
        workflowStatus: !!training.processId,
      },
      validate: (data) => {
        const errors: FormErrors<InternalInfoFormValues> = {}
        if (!data.year) {
          errors.year = i18n.t('common.empty')
        }

        return errors
      },
      onSubmit: async (data) => {
        if (data.workflowStatus && !data.processId) {
          setErrors({ processId: i18n.t('common.mandatory') })
          return {
            success: false,
            errors: [
              {
                type: 'generic_error',
                message: i18n.t('common.mandatory'),
              },
            ],
          }
        } else {
          const internalInfoResult = await updateInternalInfoTraining({
            ...data,
            id: Number(training.id),
          })
          const subsidyResult = await upsertTrainingSubsidy({
            id: Number(training.id),
            subsidy: data.fundaeSubsidized ? 'fundae' : data.subsidized ? 'other' : null,
            processId: data.workflowStatus ? data.processId : null,
            codigoCurso: data.fundaeSubsidized ? data.codigoCurso : null,
            perfil: data.fundaeSubsidized ? data.perfil : null,
            expediente: data.fundaeSubsidized ? data.expediente : null,
          })

          const subsidyHandled = handleResult(subsidyResult, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('trainings.trainings_detail.subsidy_details_not_updated'),
            })
          })

          if (!subsidyHandled.success) {
            return subsidyHandled
          }

          const handled = handleResult(internalInfoResult, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('trainings.trainings_detail.training_details_not_updated'),
            })
          })

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('trainings.trainings_detail.training_details_updated'),
            })

            resetForm({ values })
          }

          return handled
        }
      },
    })

  useEffect(() => {
    if (saved && !isSubmitting && !loading) {
      handleSubmit()
      setSaved(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, isSubmitting, loading])

  useEffect(() => {
    setIsDirty(dirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty])

  useEffect(() => {
    if (errors.year) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.year, errors.processId])

  const setOpenModalAction = (value: boolean) => {
    setOpenModal(!value)
  }

  return (
    <Box padding="s24" gap="s24">
      <Title title={i18n.t('trainings.edit_mode.internal_information.new_title')} />

      <Form onSubmit={() => handleSubmit()} padding={null}>
        <TextField
          label={i18n.t('trainings.create_training_modal.year')}
          value={String(values.year)}
          type="number"
          onChange={(value: string) => setValues({ ...values, year: Number(value) })}
          error={errors.year}
          mandatory
          readonly={readonly}
        />
        <TextField
          readonly={readonly}
          value={values.code || undefined}
          label={i18n.t('trainings.edit_mode.internal_information.code_title')}
          description={i18n.t('trainings.edit_mode.internal_information.new_code_description')}
          placeholder={i18n.t('trainings.edit_mode.internal_information.code_title')}
          onChange={(value) => setValues({ ...values, code: value })}
        />
        <SelectField
          label={i18n.t('trainings.edit_mode.internal_information.type_title')}
          options={externalOptions()}
          value={externalBoolToString(values.external)}
          onChange={(value) => {
            setValues({
              ...values,
              external: externalStringToBool(value),
            })
          }}
          readonly={readonly}
        />
        {values.external && (
          <TextField
            label={i18n.t('trainings.edit_mode.internal_information.external_provider')}
            value={values.externalProvider || ''}
            onChange={(value) => {
              setValues({ ...values, externalProvider: value })
            }}
            readonly={readonly}
          />
        )}

        <Selector.Popover
          label={i18n.t('trainings.edit_mode.internal_information.tags_title')}
          blankLabel={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.internal_information.tags_blank'
          )}
          placeholder={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.internal_information.tags_placeholder'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.internal_information.tags_description'
          )}
          isMultiSelect
          value={values.categoryIds || []}
          onChange={(ids) => {
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            setValues({ ...values, categoryIds: ids as number[] })
          }}
          inputStyle="tags"
        >
          {categories.map(({ id, name }) => (
            <Selector.Option key={id} value={id} label={name} searchValue={name}>
              <Box flexDirection="row" gap="s8" alignItems="center">
                <Text.Normal>{name}</Text.Normal>
              </Box>
            </Selector.Option>
          ))}
        </Selector.Popover>

        <Box position="sticky" gap="s16">
          {currentCompany?.legalEntities?.some((entity) => entity.country === 'es') ? (
            <FundaeToggle
              fundaeSubsidized={values.fundaeSubsidized}
              onChange={(newData) => {
                setValues({ ...values, ...newData })
              }}
              codigoCurso={values.codigoCurso}
              expediente={values.expediente}
              perfil={values.perfil}
            />
          ) : (
            <SubsidizeToggle
              onChange={(newData) => {
                setValues({ ...values, ...newData })
              }}
              subsidized={values.subsidized}
            />
          )}
          <WorkflowToggle
            defaultFundaeWorkflow={values.fundaeSubsidized ? fundaeProcess?.id : undefined}
            from="edition"
            workflowStatus={values.workflowStatus}
            fundaeToggleStatus={values.fundaeSubsidized}
            onChange={(newData) => {
              setValues({ ...values, ...newData })
            }}
            setOpen={training.processId ? setOpenModalAction : undefined}
            processId={values.processId}
            processes={processes}
            validationError={errors.processId}
          />
        </Box>
      </Form>
      {openModal && (
        <UnlinkWorkflowsModal
          setOpen={setOpenModal}
          onChange={(newData) => {
            setValues({ ...values, ...newData })
          }}
        />
      )}
    </Box>
  )
}
